/**
 * Google Sheets Budget Repository
 *
 * Google Sheets API를 사용하여 실제 스프레드시트에서 예산 데이터를 관리합니다.
 *
 * 핵심 기능:
 * - Template Priority System: 우선순위에 따라 가장 구체적인 예산 적용
 *   - Priority 3: 특정 년+월 (가장 구체적)
 *   - Priority 2: 특정 년+null월 (연도 템플릿)
 *   - Priority 1: null년+null월 (전역 템플릿)
 */

import type { Budget } from '../types/Budget'
import type { BudgetRepository, BudgetFilter } from './BudgetRepository'
import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import { SHEET_NAMES } from '@/core/config/constants'
import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'

/**
 * Spreadsheet 행을 Budget 객체로 변환
 *
 * 컬럼 순서: id, categoryId, parentBudgetId, amount, year, month, isActive
 */
function rowToBudget(row: string[]): Budget {
  return {
    id: row[0] || '',
    categoryId: row[1] || '',
    parentBudgetId: row[2] || undefined,
    amount: parseInt(row[3] || '0', 10),
    year: row[4] ? parseInt(row[4], 10) : null,
    month: row[5] ? parseInt(row[5], 10) : null,
    isActive: row[6] === 'true' || row[6] === 'TRUE',
  }
}

/**
 * Budget 객체를 Spreadsheet 행으로 변환
 *
 * 컬럼 순서: id, categoryId, parentBudgetId, amount, year, month, isActive
 */
function budgetToRow(budget: Budget): string[] {
  const row = [
    budget.id,
    budget.categoryId,
    budget.parentBudgetId || '',
    budget.amount.toString(),
    budget.year !== null ? budget.year.toString() : '',
    budget.month !== null ? budget.month.toString() : '',
    budget.isActive.toString(),
  ]
  console.log('[budgetToRow] 변환:', { input: budget, output: row })
  return row
}

/**
 * Google Sheets Budget Repository 구현
 */
export class GoogleSheetsBudgetRepository implements BudgetRepository {
  constructor(_client?: GoogleSheetsClient) {
    // client 파라미터는 테스트 호환성을 위해 유지하지만 사용하지 않음
  }

  /**
   * 현재 워크스페이스의 GoogleSheetsClient 가져오기
   */
  private getClient(): GoogleSheetsClient {
    return getWorkspaceClient()
  }

  /**
   * 필터 조건에 맞는 예산 목록 조회
   *
   * Template Priority System:
   * - year/month 필터링 시 템플릿을 포함하여 조회
   * - categoryId별로 가장 우선순위가 높은 예산만 반환
   */
  async findAll(filter?: BudgetFilter): Promise<Budget[]> {
    // 1. 모든 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.BUDGETS)
    let result = rows
      .slice(1)
      .filter((row) => row.length > 0)
      .map(rowToBudget)

    // 2. year/month 필터 + 템플릿 로직
    if (filter && (filter.year !== undefined || filter.month !== undefined)) {
      const targetYear = filter.year
      const targetMonth = filter.month

      // 2a. 템플릿 포함 필터링
      result = result.filter(b => {
        // year 체크: 특정 year 또는 템플릿(null)
        const matchYear = targetYear !== undefined
          ? (b.year === targetYear || b.year === null)
          : true

        // month 체크: 특정 month 또는 템플릿(null)
        const matchMonth = targetMonth !== undefined
          ? (b.month === targetMonth || b.month === null)
          : true

        return matchYear && matchMonth
      })

      // 2b. categoryId별 중복 제거 (우선순위 높은 것만)
      const categoryBudgets = new Map<string, Budget>()
      for (const budget of result) {
        const existing = categoryBudgets.get(budget.categoryId)
        if (!existing) {
          categoryBudgets.set(budget.categoryId, budget)
          continue
        }

        const currentPriority = this.getBudgetPriority(budget, targetYear, targetMonth)
        const existingPriority = this.getBudgetPriority(existing, targetYear, targetMonth)

        if (currentPriority > existingPriority) {
          categoryBudgets.set(budget.categoryId, budget)
        }
      }
      result = Array.from(categoryBudgets.values())
    }

    // 3. 기타 필터
    if (filter?.categoryId) {
      result = result.filter(b => b.categoryId === filter.categoryId)
    }
    if (filter?.parentBudgetId !== undefined) {
      result = result.filter(b => b.parentBudgetId === filter.parentBudgetId)
    }
    if (filter?.isActive !== undefined) {
      result = result.filter(b => b.isActive === filter.isActive)
    }

    return result
  }

  /**
   * ID로 특정 예산 조회
   */
  async findById(id: string): Promise<Budget | null> {
    const rows = await this.getClient().readSheet(SHEET_NAMES.BUDGETS)
    const row = rows.slice(1).find((row) => row[0] === id)
    return row ? rowToBudget(row) : null
  }

  /**
   * 새 예산 생성
   */
  async create(input: Omit<Budget, 'id'>): Promise<Budget> {
    console.log('[Repository] create 호출:', input)

    // ID 생성
    const rows = await this.getClient().readSheet(SHEET_NAMES.BUDGETS)
    const dataRows = rows.slice(1)
    const maxId = dataRows
      .map(row => parseInt(row[0]?.replace('budget_', '') || '0'))
      .reduce((max, current) => Math.max(max, current), 0)
    const newId = `budget_${String(maxId + 1).padStart(3, '0')}`

    const newBudget: Budget = { id: newId, ...input }
    console.log('[Repository] 새 예산 객체:', newBudget)

    const row = budgetToRow(newBudget)
    console.log('[Repository] 변환된 row:', row)

    await this.getClient().appendSheet(SHEET_NAMES.BUDGETS, [row])
    console.log('[Repository] appendSheet 완료')

    return newBudget
  }

  /**
   * 예산 수정
   */
  async update(id: string, input: Partial<Budget>): Promise<Budget> {
    const rows = await this.getClient().readSheet(SHEET_NAMES.BUDGETS)
    const dataRows = rows.slice(1)
    const rowIndex = dataRows.findIndex((row) => row[0] === id)

    if (rowIndex === -1) {
      throw new Error(`Budget not found: ${id}`)
    }

    const existingRow = dataRows[rowIndex]!
    const existing = rowToBudget(existingRow)
    const updated: Budget = { ...existing, ...input, id }

    const sheetRowIndex = rowIndex + 2
    const range = `A${sheetRowIndex}:G${sheetRowIndex}`
    await this.getClient().updateSheet(SHEET_NAMES.BUDGETS, range, [budgetToRow(updated)])

    return updated
  }

  /**
   * 예산 삭제 (Soft Delete)
   */
  async delete(id: string): Promise<void> {
    await this.update(id, { isActive: false })
  }

  /**
   * parentBudgetId로 예산 조회 (cascade 작업용)
   * @param parentBudgetId 부모 예산 ID
   * @returns 자식 예산 배열 (활성화된 것만)
   */
  async findByParentBudgetId(parentBudgetId: string): Promise<Budget[]> {
    return this.findAll({ parentBudgetId, isActive: true })
  }

  /**
   * 예산 우선순위 계산
   *
   * @param budget 예산 객체
   * @param targetYear 조회할 year
   * @param targetMonth 조회할 month
   * @returns 우선순위 점수 (0-3)
   */
  private getBudgetPriority(
    budget: Budget,
    targetYear?: number | null,
    targetMonth?: number | null
  ): number {
    // 특정 year + 특정 month = 3 (최우선)
    if (budget.year === targetYear && budget.month === targetMonth) {
      return 3
    }

    // 특정 year + month: null = 2 (해당 연도 템플릿)
    if (budget.year === targetYear && budget.month === null) {
      return 2
    }

    // year: null + month: null = 1 (완전 템플릿)
    if (budget.year === null && budget.month === null) {
      return 1
    }

    // 매칭 안 됨
    return 0
  }
}
